import { COLLECTIONS } from "constants/action-types";
import { formatMessage } from "lib/globalize-util";
import { snackbarAdd } from "actions/snackbars";
import { Observable } from "rxjs";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import { fromJS } from "immutable";
import { stringify } from "query-string";
import groupBy from "lodash/groupBy";
import { buildServerRollupSchema } from "helpers/server-rollup-schema";
import { stream } from "lib/ndjson-rxjs";
import { storeAllocationDimensions } from "helpers/collection-objects";

export const fetchRollupCollectionObjectEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ROLLUP_OBJECT.FETCH.START)
        .map(({ payload }) => payload)
        .concatMap(({ rollup_snapshot_id, collection_id, object_id, merchant_id, revision }) => {
            return ajax({
                method: "GET",
                url: `${
                    siteConfig.goAPI
                }/v1/collections/rollup_snapshots/${rollup_snapshot_id}/collections/${collection_id}/collection_objects/${object_id}?${stringify(
                    {
                        merchant_id,
                        revision,
                    }
                )}`,
            })
                .map(({ response }) => {
                    const views = response.object.mutable_list.values.reduce((accumulator, view) => {
                        accumulator[view.struct_value.id] = fromJS(view.struct_value);
                        return accumulator;
                    }, {});
                    return {
                        payload: views,
                        type: COLLECTIONS.COLLECTION.ROLLUP_OBJECT.FETCH.COMPLETED,
                    };
                })
                .catch((error) => {
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: COLLECTIONS.COLLECTION.ROLLUP_OBJECT.FETCH.COMPLETED,
                        }),
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error fetching your view. Please try again."),
                            })
                        )
                    );
                });
        });

const SR_STREAM_PAGE_LIMIT = 200;
export const streamRollupCollectionBatchObjectEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ROLLUP_OBJECT.OBJECT_STREAM.STREAM)
        .map(({ payload }) => payload)
        .switchMap(
            ({
                rollup_snapshot_id,
                merchant_id,
                snapshotParams,
                allocationDimensions,
                allocationDimensionsOrder,
                start_ref_open,
                merchantDoorClassifications,
            }) => {
                return Observable.timer(1) // hack to make this thing keep going
                    .switchMap(() => {
                        return stream(
                            `${siteConfig.goAPI}/v1/collections/rollup_snapshots/${rollup_snapshot_id}/streaming_read_objects`,
                            {
                                method: "POST",
                                postData: JSON.stringify({
                                    merchant_id,
                                    rollup_snapshot_id,
                                    object_type: "ALLOCATION_DIMENSION", // ITEM_DISPLAY_PREFERENCE: 0, ALLOCATION_DIMENSION: 1
                                    start_ref_open,
                                    limit: SR_STREAM_PAGE_LIMIT,
                                }),
                                xhrFactory: () => {
                                    const req = new XMLHttpRequest();
                                    req.withCredentials = true;
                                    return req;
                                },
                            }
                        )
                            .mergeMap((value) => Observable.of([value.result]))
                            .mergeAll()
                            .toArray();
                    })
                    .retryWhen((errors) => errors.delay(3000))
                    .catch((error) => {
                        return Observable.of({
                            error: true,
                            payload: error,
                            type: COLLECTIONS.COLLECTION.FETCH.COMPLETED,
                        });
                    })
                    .concatMap((items: any[]) => {
                        // There are scenarios, perhaps deploy issues where items is an object and it must to be an array
                        if (!Array.isArray(items)) {
                            return Observable.of({
                                error: true,
                                payload: new Error("Bad server response"),
                                type: COLLECTIONS.COLLECTION.FETCH.COMPLETED,
                            });
                        }
                        const last = items[items.length - 1];
                        const allocationDimensionsStore = (allocationDimensions || []).concat(items);

                        if (items.length === 0 || items.length < SR_STREAM_PAGE_LIMIT) {
                            return Observable.fromPromise(
                                storeAllocationDimensions(allocationDimensionsStore)
                            ).concatMap(() => {
                                const groups = groupBy(allocationDimensionsStore, "ref.document_id");

                                const allocation_dimensions = allocationDimensionsOrder.map(
                                    ({ allocationDimensions, collectionId }) => {
                                        return {
                                            data: allocationDimensions.map((dimensionId) => {
                                                return groups[collectionId].find(
                                                    (dimension) => dimension.ref.object_id === dimensionId
                                                );
                                            }),
                                        };
                                    }
                                );

                                const { rollupSnapshot, ...restParams } = snapshotParams;
                                return Observable.of({
                                    type: COLLECTIONS.COLLECTION.FETCH.INTEGRATE,
                                    payload: {
                                        ...restParams,
                                        collections: [
                                            buildServerRollupSchema(
                                                {
                                                    ...rollupSnapshot,
                                                    allocation_dimensions,
                                                },
                                                merchantDoorClassifications
                                            ),
                                        ],
                                    },
                                });
                            });
                        }

                        return Observable.of({
                            type: COLLECTIONS.COLLECTION.ROLLUP_OBJECT.OBJECT_STREAM.STREAM,
                            payload: {
                                rollup_snapshot_id,
                                merchant_id,
                                snapshotParams,
                                start_ref_open: last.ref,
                                allocationDimensions: allocationDimensionsStore,
                                allocationDimensionsOrder,
                                merchantDoorClassifications,
                            },
                        });
                    });
            }
        );
};
